import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/context/AuthContext";

export default function AdminLesson() {
  const [data, setData] = useState(null);
  const [library, setLibrary] = useState([]);
  const [learners, setLearners] = useState([]);
  const [learnersLoadState, setLearnersLoadState] = useState("idle");
  const [learnersLoadError, setLearnersLoadError] = useState("");
  const [form, setForm] = useState({ purpose: "", practical_activity: "", reflection: "" });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadAudit = () => {
    api.get("/admin/audit-log").then((res) => setAudit(res.data.entries)).catch(() => {});
  };

  useEffect(() => {
    api.get("/admin/lesson")
      .then((res) => {
        setData(res.data);
        setForm({
          purpose: res.data.content.purpose,
          practical_activity: res.data.content.practical_activity,
          reflection: (res.data.content.reflection || []).join("\n"),
        });
      })
      .catch((err) => setError(formatApiError(err)));
    api.get("/admin/lessons")
      .then((res) => setLibrary(res.data.lessons || []))
      .catch(() => {});
    api
      .get("/admin/learners")
      .then((res) => {
        setLearners(res.data.learners || []);
        setLearnersLoadState("ok");
        setLearnersLoadError("");
      })
      .catch((err) => {
        setLearners([]);
        setLearnersLoadState("error");
        setLearnersLoadError(formatApiError(err) || "Could not load learners.");
      });
    loadAudit();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    setBusy(true);
    try {
      const payload = {
        purpose: form.purpose,
        practical_activity: form.practical_activity,
        reflection: form.reflection.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      const { data: res } = await api.put("/admin/lesson", payload);
      setMessage(
        res.changed_fields.length
          ? `Saved. Changed fields: ${res.changed_fields.join(", ")}.`
          : "Saved. No changes detected."
      );
      loadAudit();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (error && !data) return <p role="alert" className="text-destructive" data-testid="admin-error">{error}</p>;
  if (!data) return <p data-testid="admin-loading">Loading admin editor…</p>;

  return (
    <div className="space-y-8" data-testid="admin-lesson-page">
      <header className="space-y-1">
        <h1 className="text-4xl sm:text-5xl font-bold text-primary">Admin — Lesson content</h1>
        <p className="text-base md:text-lg text-muted-foreground" data-testid="admin-lesson-title">
          {data.lesson.title}
        </p>
        <p className="text-sm">
          <Link to="/admin/learners" className="underline" data-testid="admin-learners-oversight-link">
            Learner oversight (filters &amp; lifecycle)
          </Link>
          {" · "}
          <Link to="/admin/instructors" className="underline" data-testid="admin-instructors-link">
            Instructor onboarding (invite &amp; assign)
          </Link>
        </p>
      </header>

      <section className="space-y-3" data-testid="admin-content-status-section">
        <h2 className="text-base md:text-lg font-semibold">Practical guides — launch status</h2>
        <p className="text-sm text-muted-foreground">
          Admin-only launch decisions. Held lessons stay hidden from learners. Learner enablement is
          applied only via the controlled seed/import process — there is no one-click enable here.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-content-status-table">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2 pr-3">Lesson</th>
                <th className="py-2 pr-3">Launch status</th>
                <th className="py-2 pr-3">Learner</th>
                <th className="py-2 pr-3">Image</th>
                <th className="py-2 pr-3">Hold / remaining</th>
                <th className="py-2 pr-3">Quiz</th>
              </tr>
            </thead>
            <tbody>
              {library.map((row) => (
                <tr key={row.lesson.id} className="border-b align-top">
                  <td className="py-2 pr-3">
                    <Link to={`/lessons/${row.lesson.id}`} className="underline-offset-2 hover:underline">
                      {row.lesson.title}
                    </Link>
                    {row.target_guide && (
                      <div className="text-xs text-muted-foreground">{row.target_guide}</div>
                    )}
                  </td>
                  <td className="py-2 pr-3">{row.launch_status || row.content_status}</td>
                  <td className="py-2 pr-3">{row.learner_enabled ? "enabled" : "held"}</td>
                  <td className="py-2 pr-3">{row.image_decision || row.image_status}</td>
                  <td className="py-2 pr-3 text-xs max-w-xs">
                    {row.hold_reason || row.remaining_action || "—"}
                  </td>
                  <td className="py-2 pr-3 text-xs">
                    {row.quiz_seeded
                      ? `${row.quiz_question_count || 0} Q · ${row.quiz_learner_enabled ? "learner" : "admin-only"}`
                      : "none"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <form onSubmit={save} className="card space-y-5" data-testid="admin-edit-form">
        <h2 className="text-base md:text-lg font-semibold">Edit Chapter 4 controlled fields</h2>
        <div className="space-y-1">
          <label htmlFor="edit-purpose" className="font-medium">Purpose</label>
          <textarea
            id="edit-purpose"
            data-testid="admin-edit-purpose"
            className="input min-h-[140px]"
            value={form.purpose}
            onChange={(e) => setForm({ ...form, purpose: e.target.value })}
            required
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="edit-activity" className="font-medium">Practical activity</label>
          <textarea
            id="edit-activity"
            data-testid="admin-edit-activity"
            className="input min-h-[100px]"
            value={form.practical_activity}
            onChange={(e) => setForm({ ...form, practical_activity: e.target.value })}
            required
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="edit-reflection" className="font-medium">Reflection questions (one per line)</label>
          <textarea
            id="edit-reflection"
            data-testid="admin-edit-reflection"
            className="input min-h-[100px]"
            value={form.reflection}
            onChange={(e) => setForm({ ...form, reflection: e.target.value })}
            required
          />
        </div>
        {error && <p role="alert" className="text-destructive" data-testid="admin-save-error">{error}</p>}
        {message && <p role="status" data-testid="admin-save-message">{message}</p>}
        <div className="flex flex-wrap gap-3">
          <button type="submit" className="btn-primary" disabled={busy} data-testid="admin-save-button">
            {busy ? "Saving…" : "Save changes"}
          </button>
          <Link to="/lessons/lesson-ch4-mirror-setup" className="btn-outline" data-testid="admin-view-lesson-link">
            View Chapter 4
          </Link>
        </div>
      </form>

      <section className="space-y-3" data-testid="admin-learners-section">
        <h2 className="text-base md:text-lg font-semibold">Learners</h2>
        <p className="text-sm text-muted-foreground">
          Self-registered and legacy learners. Source helps later referral attribution.
        </p>
        {learnersLoadState === "error" && (
          <p role="alert" className="text-destructive" data-testid="admin-learners-load-error">
            {learnersLoadError} (this is not an empty roster — the request failed.)
          </p>
        )}
        {learnersLoadState !== "error" && learners.length === 0 && learnersLoadState === "ok" && (
          <p className="text-muted-foreground" data-testid="admin-learners-empty">No learners yet.</p>
        )}
        {learnersLoadState === "idle" && learners.length === 0 && (
          <p className="text-muted-foreground" data-testid="admin-learners-loading">Loading learners…</p>
        )}
        {learners.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-learners-table">
              <thead>
                <tr className="text-left border-b border-border">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3">Verified</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Source</th>
                  <th className="py-2 pr-3">Enrolled</th>
                  <th className="py-2">Signed up</th>
                </tr>
              </thead>
              <tbody>
                {learners.map((learner) => (
                  <tr key={learner.id} className="border-b border-border/60" data-testid={`admin-learner-${learner.id}`}>
                    <td className="py-2 pr-3">{learner.display_name || "—"}</td>
                    <td className="py-2 pr-3">{learner.email}</td>
                    <td className="py-2 pr-3">{learner.email_verified ? "Yes" : "No"}</td>
                    <td className="py-2 pr-3">{learner.account_status || "—"}</td>
                    <td className="py-2 pr-3">{learner.registration_source || "—"}</td>
                    <td className="py-2 pr-3">{learner.enrolment_status || "—"}</td>
                    <td className="py-2">
                      {learner.created_at ? new Date(learner.created_at).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-3" data-testid="audit-log-section">
        <h2 className="text-base md:text-lg font-semibold">Audit log</h2>
        {audit.length === 0 ? (
          <p className="text-muted-foreground" data-testid="audit-log-empty">No edits recorded yet.</p>
        ) : (
          <ul className="space-y-3">
            {audit.map((entry) => (
              <li key={entry.id} className="card space-y-1" data-testid={`audit-entry-${entry.id}`}>
                <p className="text-sm font-medium">
                  {entry.admin_email} — {entry.action} — {new Date(entry.created_at).toLocaleString()}
                </p>
                <p className="text-sm text-muted-foreground">
                  Fields changed: {Object.keys(entry.changes || {}).join(", ") || "none"}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
